import qs from 'qs'

import {
  type StrapiArticleType,
  type StrapiContentPageType,
  type StrapiContentSectionType,
  type StrapiShortVideoType,
  type VideoLessonType,
} from '@/legacy-src/components/hedepyPlus/types'
import { appContentPopulate } from '@/legacy-src/components/pageBlocks/populate'
import { type StrapiCaseStudyType, type TestimonialType } from '@/legacy-src/components/pageBlocks/types'
import { type StrapiTestType } from '@/legacy-src/components/tests/types'
import { LOCALE, type Locale } from '@/legacy-src/constants/locale.constants'
import {
  type PageCategoryType,
  type PromotionsBarType,
  type StrapiCompanyPageType,
  type StrapiImageDimensions,
  type StrapiObject,
  type StrapiSlugLocalisations,
} from '@/legacy-src/lib/strapi/types'

import { fetchWithRetry } from '@/src/utils/fetchWithRetry'

import { strapiFetchRetryOptions } from '@/src/domains/strapi/constants'
import { getStrapiCounsellingPageSlugTag } from '@/src/domains/strapi/services/fetchStrapiCounsellingPage'

import { type StrapiPageType } from './types'

// sending response format allows us to use the current queries even after the Strapi update
export const STRAPI_RESPONSE_FORMAT = 'v4'

export const fetchFromStrapiRestSingleCall = async <R = any>(
  path: string,
  params: Record<string, any> = {},
  cacheParam: RequestCache = 'force-cache',
  logResponse: boolean = false,
  cacheTags?: string[],
) => {
  const url = `${process.env.NEXT_PUBLIC_CMS_URL}/api/${path}?${qs.stringify({
    locale: LOCALE,
    ...params,
  })}`
  const cache: RequestCache = (process.env.NEXT_PUBLIC_CMS_CACHE as RequestCache) || cacheParam
  const res = await fetchWithRetry(
    url,
    {
      cache,
      next: {
        tags: cacheTags,
      },
      headers: { 'Strapi-Response-Format': STRAPI_RESPONSE_FORMAT },
    },
    strapiFetchRetryOptions,
  )
  if (logResponse) {
    console.log(res)
  }
  if (!res.ok) {
    const responseText = await res.text()
    const errorMessage = `Strapi fetch error: ${res.status} ${res.statusText} for path: ${path} ${JSON.stringify(params.filters)} with text: ${responseText}`
    console.error(errorMessage)
    throw new Error(errorMessage)
  }
  const json = await res.json()
  return json.data as R
}

/**
 * Fetches data from Strapi REST API, handling pagination if necessary.
 * The strapi fetches are cached, thus it doesn't overload the Strapi instance.
 */
export const fetchFromStrapiRest = async <R = any>(
  path: string,
  params: Record<string, any> = {},
  cacheParam: RequestCache = 'force-cache',
  logResponse: boolean = false,
  cacheTags?: string[],
) => {
  // In case pagination is already provided, do a single call
  if ('pagination' in params && typeof params.pagination === 'object' && 'pageSize' in params.pagination) {
    const data = await fetchFromStrapiRestSingleCall<R>(path, params, cacheParam, logResponse, cacheTags)
    return data as R
  }

  let arrayData: any[] = []
  const pageSize = 100
  let page = 1

  do {
    const data = await fetchFromStrapiRestSingleCall<R>(
      path,
      {
        pagination: {
          pageSize,
          page,
        },
        ...params,
      },
      cacheParam,
      logResponse,
      cacheTags,
    )

    // In case data returned is an array, try to follow the pages
    if (Array.isArray(data)) {
      arrayData = [...arrayData, ...data]

      // There is less data than pageSize, thus there won't be more
      if (data.length < pageSize) {
        return arrayData as R
      } else {
        page += 1
      }
    } else {
      return data as R
    }
  } while (true)
}

// --- Interative Lessons ---
// these are the "instagram-style" clickable lessons
// unfortunately, the actual name is constantly changing in product
// this may also be reflected in the codebase
export const fetchBlendedCareLesson = async (slug: string) => {
  const res = await fetchFromStrapiRest('blended-care-lessons', {
    populate: {
      image: true,
      steps: {
        // this should work in v5, but keeping the old populate for now
        populate: '*',
        // populate: {
        //   image: true,
        //   poll: { populate: ['image'] },
        //   questions: { populate: ['image'] },
        //   audio: true,
        //   video: true,
        //   bubbles: true,
        //   followup: true,
        // },
      },
    },
    filters: { slug: { $eq: slug } },
  })
  return res[0] ? { ...res[0].attributes, id: res[0].id } : undefined
}
export const fetchAllBlendedCareCoursesFromStrapi = async () =>
  fetchFromStrapiRest('blended-care-courses', {
    populate: { blended_care_lessons: { populate: 'image' }, image: true },
    // this shows quick_help course first
    sort: ['type:desc'],
  })
export const fetchBlendedCareCourse = async (slug: string) => {
  const res = await fetchFromStrapiRest('blended-care-courses', {
    populate: { blended_care_lessons: { populate: { image: true } }, image: true, expertPhoto: true },
    filters: { slug: { $eq: slug } },
  })
  return res[0] ? { ...res[0].attributes, id: res[0].id } : undefined
}

// --- Articles ---
// please note the legacy 'counselling-pages' endpoint
// this is a strapi entity name that cannot be changed
// it may pop-up elsewhere in the codebase
const articleTeaserProps = {
  populate: ['cover', 'seo'],
  sort: ['isPromoted:desc', 'order', 'id:desc'],
}
const businessFilter = { type: { $eq: 'business' } }

const publicFilter = { $or: [{ type: { $eq: 'public' } }, { type: { $null: true } }] }
const articleProps = {
  populate: {
    seo: { populate: '*' },
    cover: { populate: '*' },
    author: { populate: '*' },
    content: { populate: '*' },
    allowedCompanyIds: { populate: '*' },
  },
}
const fetchAllArticleTeasersFromStrapi = async (
  filters: Record<string, any>,
): Promise<StrapiObject<StrapiArticleType>[]> =>
  fetchFromStrapiRest('counselling-pages', {
    ...articleTeaserProps,
    filters: filters,
  })
export const fetchAllPublicArticleTeasersFromStrapi = async () => fetchAllArticleTeasersFromStrapi(publicFilter)
export const fetchAllBusinessArticleTeasersFromStrapi = async () => fetchAllArticleTeasersFromStrapi(businessFilter)
type WebinarFilter = 'upcoming' | 'past' | 'all'

export const fetchAllWebinarTeasersFromStrapi = async (
  filter: WebinarFilter = 'all',
  limit?: number,
): Promise<StrapiObject<StrapiArticleType>[]> => {
  const today = new Date().toISOString()

  const getFilters = () => {
    switch (filter) {
      case 'upcoming':
        return {
          $and: [
            { type: { $eq: 'webinar' } },
            { eventState: { $ne: 'hidden' } },
            { eventState: { $eq: 'upcoming' } },
            { eventDate: { $gt: today } },
          ],
        }
      case 'past':
        return {
          $and: [
            { type: { $eq: 'webinar' } },
            { eventState: { $ne: 'hidden' } },
            { eventState: { $notNull: true } },
            { eventState: { $ne: 'upcoming' } },
          ],
        }
      default:
        // 'all' filter: includes both upcoming (with future eventDate) and past webinars
        return {
          $and: [
            { type: { $eq: 'webinar' } },
            { eventState: { $ne: 'hidden' } },
            { eventState: { $notNull: true } },
            {
              $or: [
                { eventState: { $ne: 'upcoming' } },
                {
                  $and: [{ eventState: { $eq: 'upcoming' } }, { eventDate: { $gt: today } }],
                },
              ],
            },
          ],
        }
    }
  }

  const getSort = () => {
    switch (filter) {
      case 'upcoming':
        return ['eventDate:asc']
      case 'past':
        return ['eventDate:desc']
      default:
        return undefined // Will sort manually below for 'all'
    }
  }

  const webinars = await fetchFromStrapiRest<StrapiObject<StrapiArticleType>[]>('counselling-pages', {
    populate: ['cover', 'seo', 'allowedCompanyIds'],
    filters: getFilters(),
    ...(getSort() && { sort: getSort() }),
    ...(limit && { pagination: { limit } }),
  })

  // For 'all' filter, maintain backward-compatible sorting
  if (filter === 'all') {
    webinars.sort((a, b) => {
      if (a.attributes.eventState === 'upcoming' && b.attributes.eventState !== 'upcoming') return -1
      if (a.attributes.eventState !== 'upcoming' && b.attributes.eventState === 'upcoming') return 1

      if (a.attributes.eventDate && b.attributes.eventDate) {
        const dateA = new Date(a.attributes.eventDate).getTime()
        const dateB = new Date(b.attributes.eventDate).getTime()

        // Sort upcoming events ascending
        if (a.attributes.eventState === 'upcoming' && b.attributes.eventState === 'upcoming') {
          return dateA - dateB
        }
        // Sort everything else descending
        return dateB - dateA
      }
      return 0
    })
  }

  return webinars
}
export const fetchAllArticleTeasersForCategory = async (category: string, isBusinessPage: boolean) =>
  fetchAllArticleTeasersFromStrapi({
    ...(isBusinessPage ? businessFilter : publicFilter),
    category: { $eq: category },
  })
const fetchArticleFromStrapi = async (slug: string, filters: Record<string, any>) => {
  const decodedSlug = decodeURIComponent(slug)
  const res = await fetchFromStrapiRest(
    'counselling-pages',
    {
      ...articleProps,
      filters: {
        ...filters,
        slug: { $eq: decodedSlug },
      },
    },
    'force-cache',
    false,
    [getStrapiCounsellingPageSlugTag(decodedSlug)],
  )
  return res[0]?.attributes
}
export const fetchPublicArticleFromStrapi = async (slug: string) => fetchArticleFromStrapi(slug, publicFilter)
export const fetchBusinessArticleFromStrapi = async (slug: string) => fetchArticleFromStrapi(slug, businessFilter)
export const fetchWebinarFromStrapi = async (slug: string) => {
  const today = new Date().toISOString()
  return fetchArticleFromStrapi(slug, {
    $and: [
      { type: { $eq: 'webinar' } },
      { eventState: { $ne: 'hidden' } },
      { eventState: { $notNull: true } },
      {
        $or: [
          { eventState: { $ne: 'upcoming' } },
          {
            $and: [{ eventState: { $eq: 'upcoming' } }, { eventDate: { $gt: today } }],
          },
        ],
      },
    ],
  })
}

// --- Pages ---
// pages are simple entities that link an app-content which contains the actual content
// app-content needs to be populated
const allPageProps = { populate: 'seo' }
export const fetchAllPublicPagesFromStrapi = async () =>
  fetchFromStrapiRest('pages', {
    ...allPageProps,
    filters: { $or: [{ category: { $eq: 'public' } }, { category: { $null: true } }] },
  })
export const fetchAllBusinessPagesFromStrapi = async () =>
  fetchFromStrapiRest('pages', { ...allPageProps, filters: { category: { $eq: 'business' } } })
// Helper to fetch page details without blocks
const fetchPageDetails = async (slug: string, category: PageCategoryType) => {
  const filters: Record<string, any> = { slug: { $eq: decodeURIComponent(slug) } }
  if (category === 'business') filters.category = { $eq: 'business' }
  if (category === 'public') filters.$or = [{ category: { $eq: 'public' } }, { category: { $null: true } }]
  const res = await fetchFromStrapiRest('pages', {
    filters,
    populate: '*',
  })
  return res?.[0]
}
const fetchBlocksContentFromStrapi = async (pageBlocks: any[]) => {
  if (!Array.isArray(pageBlocks)) return []
  return Promise.all(
    pageBlocks.map(async (block) => {
      if (block?.attributes && block.documentId) {
        block.attributes.content = await fetchAppContentFromStrapiByDocumentId(block.documentId)
      }
      return block
    }),
  )
}

export const fetchPageFromStrapi = async (
  slug: string,
  category: PageCategoryType,
): Promise<{ attributes: StrapiPageType }> => {
  const details = await fetchPageDetails(slug, category)
  if (details?.attributes?.pageBlocks?.data) {
    details.attributes.pageBlocks.data = await fetchBlocksContentFromStrapi(details?.attributes.pageBlocks?.data)
  }
  return details
}

// --- App Content ---
// this is the actual content that is linked to pages
// this separate function exists if we want to display it on a non-strapi page
const fetchAppContentWithFilters = async (filters: Record<string, any>, cacheTags?: string[]) => {
  const res = await fetchFromStrapiRest(
    'app-contents',
    {
      filters: {
        ...filters,
      },
      populate: {
        content: appContentPopulate,
      },
    },
    'force-cache',
    false,
    cacheTags,
  )
  return res[0]?.attributes.content
}
export const fetchAppContentFromStrapi = async (idName: string) =>
  fetchAppContentWithFilters({ idName: { $eq: idName } }, [`app-content-idName-${idName}`])
// this is only used internally for fetching pages
// due to the duplicate bug, the idName could be duplicate in some cases
export const fetchAppContentFromStrapiByDocumentId = async (documentId: string) =>
  fetchAppContentWithFilters({ documentId: { $eq: documentId } }, [`app-content-documentId-${documentId}`])

// --- Company Page ---
// these are pages that live under the /company/:slug path
// some legacy companies live as regular pages at /:slug
export const fetchAllCompaniesFromStrapi = async () => fetchFromStrapiRest('company-pages')
export const fetchCompanyFromStrapi = async (slug: string): Promise<StrapiCompanyPageType | null> => {
  const res = await fetchFromStrapiRest(
    'company-pages',
    {
      filters: { slug: { $eq: decodeURIComponent(slug) } },
      populate: {
        listGrid: { populate: '*' },
        buttonsList: { populate: '*' },
        faqSection: { populate: '*' },
        headerImage: { populate: '*' },
        pageBlocks: { populate: '*' },
      },
    },
    'force-cache',
    false,
    ['company-page-' + slug],
  )

  if (!res[0]) {
    return null
  }

  // FIXME! Dont like this, how to make it typed better?
  const page = res[0].attributes
  let pageBlocks: any[] = []

  if (page?.pageBlocks?.data) {
    pageBlocks = (await fetchBlocksContentFromStrapi(page?.pageBlocks?.data)) as any[]
  }

  return {
    ...page,
    pageBlocks: pageBlocks.flatMap((array) => array.attributes.content),
  }
}

// --- Video Lessons ---
// this is content like Poshurshemo, Atairu, etc.
const videoLessonPopulate = {
  populate: {
    lessonInfo: { populate: { lessonAuthor: { populate: 'thumbnail' } } },
    videoSection: { populate: ['chaptersEntry'] },
  },
}
export const fetchAllVideoLessonsFromStrapi = async () =>
  fetchFromStrapiRest('online-lesson-pages', {
    ...videoLessonPopulate,
  })
export const fetchVideoLessonsByTypeFromStrapi = async (type: VideoLessonType, limit?: number) =>
  fetchFromStrapiRest('online-lesson-pages', {
    filters: {
      type: { $eq: type },
    },
    pagination: { pageSize: limit || 100 },
    ...videoLessonPopulate,
  })
export const fetchVideoLessonFromStrapi = async (slug: string) => {
  const res = await fetchFromStrapiRest('online-lesson-pages', {
    filters: { slug: { $eq: decodeURIComponent(slug) } },
    populate: {
      headerImage: true,
      videoSection: { populate: ['chaptersEntry'] },
      lessonInfo: { populate: { lessonAuthor: { populate: ['thumbnail', 'logo'] }, lessonSources: true } },
    },
  })
  return res[0]?.attributes
}

// --- Test Page ---
// these are tests like "Are you depressed?", "Are you anxious?", etc.
export const fetchAllTestsFromStrapi = async (): Promise<{ attributes: StrapiTestType }[]> =>
  fetchFromStrapiRest('test-pages', { populate: ['seo', 'image'], sort: 'order' })
export const fetchTestFromStrapi = async (slug: string) => {
  const res = await fetchFromStrapiRest('test-pages', {
    filters: { slug: { $eq: decodeURIComponent(slug) } },
    populate: {
      description: { populate: '*' },
      faq: { populate: '*' },
      seo: { populate: '*' },
      image: { populate: '*' },
    },
    //   description: true,
    //   faq: { populate: { entry: true } },
    //   seo: { populate: { metaImage: true, metaSocial: true } },
    //   image: { populate: ['data'] },
    // },
  })
  return res[0]?.attributes
}

// --- Testimonials ---
// b2b testimonials that are used at different places
export const fetchAllTestimonialsFromStrapi = async (): Promise<TestimonialType[]> => {
  const res = await fetchFromStrapiRest('testimonials', {
    populate: ['companyLogo', 'videoCoverImage', 'authorImage'],
  })
  return res?.map((item) => ({ id: item.id, ...item.attributes })) || []
}

// --- Case studies ---
export const fetchAllCaseStudiesFromStrapi = async (): Promise<StrapiCaseStudyType[]> => {
  const res = await fetchFromStrapiRest('case-studies', {
    populate: ['logo'],
  })
  return res?.map((item) => ({ id: item.id, ...item.attributes })) || []
}
export const fetchSingleCaseStudyFromStrapi = async (slug: string): Promise<StrapiCaseStudyType> => {
  const res = await fetchFromStrapiRest('case-studies', {
    filters: { slug: { $eq: decodeURIComponent(slug) } },
    populate: {
      logo: true,
      seo: { populate: { metaSocial: { populate: ['image'] }, metaImage: true } },
      content: { populate: '*' },
    },
  })
  return res[0]?.attributes
}

// --- Short videos ---
export const fetchAllShortVideosFromStrapi = async (): Promise<StrapiShortVideoType[]> => {
  const res = await fetchFromStrapiRest('short-videos', {
    populate: ['coverImage'],
    sort: ['isFeatured:desc', 'createdAt:desc'],
  })
  return res?.map((item) => ({ id: item.id, ...item.attributes })) || []
}

// --- Content Sections & Pages ---
// these are content items that use the page blocks system
const contentSectionPopulate = {
  contentPages: { populate: 'cover' },
  titleCard: { populate: { image: true } },
}
export const fetchAllContentSectionsFromStrapi = async (): Promise<StrapiContentSectionType[]> => {
  const res: StrapiObject<StrapiContentSectionType>[] = await fetchFromStrapiRest('content-sections', {
    populate: contentSectionPopulate,
    sort: ['priority:asc', 'title:asc'],
  })
  return res.map((item) => item.attributes)
}
export const fetchContentSectionFromStrapi = async (slug: string): Promise<StrapiContentSectionType> => {
  const res = await fetchFromStrapiRest('content-sections', {
    populate: contentSectionPopulate,
    filters: { slug: { $eq: decodeURIComponent(slug) } },
  })
  return res?.[0]?.attributes
}
export const fetchContentPageFromStrapi = async (slug: string): Promise<StrapiContentPageType> => {
  const res = await fetchFromStrapiRest(
    'content-pages',
    {
      filters: { slug: { $eq: decodeURIComponent(slug) } },
      populate: { cover: true, pageBlocks: true },
    },
    'force-cache',
    false,
    [`content-page-${slug}`],
  )
  const page = res?.[0]?.attributes
  if (page?.pageBlocks?.data) {
    page.pageBlocks.data = await fetchBlocksContentFromStrapi(page?.pageBlocks?.data)
  }
  return page
}

// --- Academy ---
// "academy" page for therapists at /academy
export const fetchAcademyFromStrapi = async () => {
  const res = await fetchFromStrapiRest('academies', {
    populate: {
      content: { populate: '*' },
    },
  })
  return res[0]
}

// --- Slugs Localisations ---
// if you want to know article slug in different language
export const fetchLocalisedSlugsForType = async (path: string) => {
  const res: {
    id: number
    attributes: { slug: string; localizations: { data: { attributes: { locale: string; slug: string } }[] } }
  }[] = await fetchFromStrapiRest(
    path,
    {
      populate: { localizations: { fields: ['slug', 'locale'] } },
      fields: ['slug'],
    },
    'force-cache',
  )

  return res.reduce((acc, item) => {
    acc[item.attributes.slug] = item.attributes.localizations.data.reduce(
      (acc, loc) => {
        acc[loc.attributes.locale] = loc.attributes.slug
        return acc
      },
      {} as Record<Locale, string>,
    )
    return acc
  }, {} as StrapiSlugLocalisations)
}

// --- Image Dimensions Fetching ---
// if we have image in markdown, we do not receive its dimensions
// this allows to fetch it, so the content can be rendered properly

export const fetchAllImageDimensions = async () => {
  const res = await fetch(`${process.env.NEXT_PUBLIC_CMS_URL}/api/upload/files`)
  const json = await res.json()

  const dimensionsByUrl: { [url: string]: StrapiImageDimensions } = {}

  json.forEach((imgData) => {
    dimensionsByUrl[imgData.url] = { w: imgData.width, h: imgData.height }
  })

  return dimensionsByUrl
}
export const fetchSingleImageDimensions = async (src: string): Promise<StrapiImageDimensions | null> => {
  const res = await fetch(`${process.env.NEXT_PUBLIC_CMS_URL}/api/upload/files?_url=${src}`)
  const json = await res.json()
  const file = json[0]

  return file ? { w: file.width, h: file.height } : null
}

// --- Promo Bar ---
export const fetchPromoBarFromStrapi = async (isBusinessPage: boolean = false): Promise<PromotionsBarType | null> => {
  const type = isBusinessPage ? 'businessPromotionsBar' : 'promotionsBar'
  const res = await fetchFromStrapiRest(
    'app',
    {
      populate: {
        [type]: {
          populate: '*',
        },
      },
    },
    // WARNING: This fetch call is not cached, so it will always make a network request
    // this is needed to make sure the promo bar is always up to date
    'no-store',
  )

  return res?.attributes?.[type] || null
}
